import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  QrCode,
  Calendar,
  Users,
  MapPin,
  Utensils,
  ScanLine,
  Building2,
  ShieldCheck,
  Smartphone,
  X,
} from 'lucide-react';
import {
  isNativeMobileShell,
  setSimulatedNativeMobileShell,
  triggerMobileHaptic,
} from '../lib/nativeMobile';

type MobileAppRoleMode = 'delegate' | 'exhibitor' | 'staff';

export function MobileAppTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [nativePreviewActive, setNativePreviewActive] = useState<boolean>(() =>
    isNativeMobileShell()
  );
  const [roleMode, setRoleMode] = useState<MobileAppRoleMode>('delegate');

  useEffect(() => {
    const syncMode = () => {
      setNativePreviewActive(isNativeMobileShell());
    };
    window.addEventListener('iiab-native-mode-changed', syncMode);
    window.addEventListener('storage', syncMode);
    return () => {
      window.removeEventListener('iiab-native-mode-changed', syncMode);
      window.removeEventListener('storage', syncMode);
    };
  }, []);

  // Automatically infer the best default mode from the current route or active session tokens
  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith('/checkin') || p.startsWith('/counter/perks') || p.startsWith('/vendor/perks')) {
      setRoleMode('staff');
    } else if (p.startsWith('/exhibitor-portal') || p.startsWith('/portal/exhibitor')) {
      setRoleMode('exhibitor');
    } else if (p.startsWith('/my-iiab')) {
      setRoleMode('delegate');
    }
  }, [location.pathname]);

  // Hide on desktop Admin Console unless native preview is explicitly toggled on
  if (location.pathname.startsWith('/admin') && !location.pathname.startsWith('/admin/operations') && !nativePreviewActive) {
    return null;
  }

  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get('tab') || 'card';

  const handleNavigate = (to: string) => {
    triggerMobileHaptic('light');
    navigate(to);
  };

  const handleSwitchRoleMode = (nextMode: MobileAppRoleMode) => {
    triggerMobileHaptic('light');
    setRoleMode(nextMode);
    if (nextMode === 'delegate') {
      navigate('/my-iiab?tab=card');
    } else if (nextMode === 'exhibitor') {
      navigate('/exhibitor-portal');
    } else {
      navigate('/checkin');
    }
  };

  const delegateTabs = [
    {
      id: 'card',
      label: 'Pass & ID',
      icon: QrCode,
      to: '/my-iiab?tab=card',
      active: location.pathname.startsWith('/my-iiab') && (currentTab === 'card' || !searchParams.get('tab')),
    },
    {
      id: 'agenda',
      label: 'Agenda',
      icon: Calendar,
      to: '/my-iiab?tab=agenda',
      active: location.pathname.startsWith('/my-iiab') && currentTab === 'agenda',
    },
    {
      id: 'meetings',
      label: 'B2B & Scan',
      icon: Users,
      to: '/my-iiab?tab=meetings',
      active:
        (location.pathname.startsWith('/my-iiab') && (currentTab === 'meetings' || currentTab === 'connections')) ||
        location.pathname.startsWith('/b/'),
    },
    {
      id: 'floorplan',
      label: 'Floor Plan',
      icon: MapPin,
      to: '/floor-plan',
      active: location.pathname.startsWith('/floor-plan'),
    },
    {
      id: 'perks',
      label: 'Perks',
      icon: Utensils,
      to: '/my-iiab?tab=perks',
      active: location.pathname.startsWith('/my-iiab') && currentTab === 'perks',
    },
  ];

  const exhibitorTabs = [
    {
      id: 'scanner',
      label: 'Scan Lead',
      icon: ScanLine,
      to: '/exhibitor-portal?tab=leads',
      active: location.pathname.startsWith('/exhibitor-portal') && (!searchParams.get('tab') || currentTab === 'leads'),
    },
    {
      id: 'meetings',
      label: 'B2B Meetings',
      icon: Users,
      to: '/exhibitor-portal?tab=meetings',
      active: location.pathname.startsWith('/exhibitor-portal') && currentTab === 'meetings',
    },
    {
      id: 'passes',
      label: 'Booth Passes',
      icon: Building2,
      to: '/exhibitor-portal?tab=passes',
      active: location.pathname.startsWith('/exhibitor-portal') && currentTab === 'passes',
    },
    {
      id: 'floorplan',
      label: 'Floor Map',
      icon: MapPin,
      to: '/floor-plan',
      active: location.pathname.startsWith('/floor-plan'),
    },
  ];

  const staffTabs = [
    {
      id: 'gate',
      label: 'Gate Scanner',
      icon: ScanLine,
      to: '/checkin',
      active: location.pathname.startsWith('/checkin'),
    },
    {
      id: 'perks-kiosk',
      label: 'Perks Counter',
      icon: Utensils,
      to: '/counter/perks',
      active: location.pathname.startsWith('/counter/perks') || location.pathname.startsWith('/vendor/perks'),
    },
    {
      id: 'live-ops',
      label: 'Helpdesk Ops',
      icon: ShieldCheck,
      to: '/admin/operations',
      active: location.pathname.startsWith('/admin/operations'),
    },
  ];

  const activeTabs =
    roleMode === 'exhibitor'
      ? exhibitorTabs
      : roleMode === 'staff'
      ? staffTabs
      : delegateTabs;

  return (
    <div
      className={`fixed bottom-0 inset-x-0 z-50 ${
        nativePreviewActive ? 'block' : 'md:hidden'
      }`}
    >
      {/* Role-Adaptive Mode Switcher Ribbon when Native App Shell Preview is active */}
      {nativePreviewActive && (
        <div className="bg-[#062B1B] text-white border-t border-emerald-800/80 px-3 py-1.5 flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-1.5 font-semibold text-[#E6C687]">
            <Smartphone className="w-3.5 h-3.5" />
            <span>IIAB 2027 Mobile App Shell</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleSwitchRoleMode('delegate')}
              className={`px-2 py-0.5 rounded-full font-medium transition-colors ${
                roleMode === 'delegate'
                  ? 'bg-[#E6C687] text-[#062B1B] font-bold'
                  : 'text-emerald-200 hover:bg-emerald-900/60'
              }`}
            >
              Mode A: Delegate
            </button>
            <button
              type="button"
              onClick={() => handleSwitchRoleMode('exhibitor')}
              className={`px-2 py-0.5 rounded-full font-medium transition-colors ${
                roleMode === 'exhibitor'
                  ? 'bg-[#E6C687] text-[#062B1B] font-bold'
                  : 'text-emerald-200 hover:bg-emerald-900/60'
              }`}
            >
              Mode B: Exhibitor
            </button>
            <button
              type="button"
              onClick={() => handleSwitchRoleMode('staff')}
              className={`px-2 py-0.5 rounded-full font-medium transition-colors ${
                roleMode === 'staff'
                  ? 'bg-[#E6C687] text-[#062B1B] font-bold'
                  : 'text-emerald-200 hover:bg-emerald-900/60'
              }`}
            >
              Mode C: Staff Kiosk
            </button>
            <button
              type="button"
              onClick={() => setSimulatedNativeMobileShell(false)}
              title="Exit Mobile App Preview Mode"
              className="ml-1 p-1 text-emerald-300 hover:text-white rounded-md"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Primary Bottom Tab Bar */}
      <nav
        aria-label="IIAB Mobile Navigation"
        className="bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-4px_20px_rgba(10,61,39,0.08)] px-2 pb-[env(safe-area-inset-bottom,0px)]"
      >
        <div className="max-w-lg mx-auto grid grid-flow-col auto-cols-fr">
          {activeTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleNavigate(tab.to)}
                className={`flex flex-col items-center justify-center py-2 px-1 transition-colors focus:outline-hidden ${
                  tab.active
                    ? 'text-[#0A3D27] font-bold'
                    : 'text-slate-500 hover:text-slate-800 font-medium'
                }`}
              >
                <div
                  className={`p-1 rounded-xl transition-colors ${
                    tab.active ? 'bg-[#0A3D27]/10 text-[#0A3D27]' : ''
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-[10px] leading-tight mt-0.5 truncate max-w-full">
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default MobileAppTabBar;
